import { useState, useEffect, useCallback, useRef } from "react";
import { useAuth } from "../contexts/AuthContext";
import axios from "axios";
import {
  Trophy, Target, CheckCircle, XCircle, Clock, ArrowRight,
  Loader2, ChevronDown, ChevronUp, Star, Zap, TrendingUp,
  Calendar, Users, Award, Plus, Settings, AlertTriangle, Trash2, Save, Lock, Search, Unlock, Share2, Crown, Video, RefreshCw
} from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import useCelebration from "../hooks/useCelebration";
import CelebrationOverlay from "../components/CelebrationOverlay";
import useInterstitialAd from "../hooks/useInterstitialAd";
import { hapticSuccess } from "../utils/haptics";
import { getEffectiveTier, getFreePredictionLimit, getTierLabel } from "../utils/features";
import { getDisplayName, getDisplayShortName } from "../utils/teamNameMapping";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const QUESTION_TYPE_LABELS = {
  winner: "Match Winner",
  top_scorer: "Top Run Scorer",
  top_wicket_taker: "Top Wicket Taker",
  man_of_match: "Man of the Match",
  custom: "Custom",
  range: "Range",
  yes_no: "Yes/No",
  over_under: "Over/Under",
  bonus: "Bonus",
};

const VALID_POINTS = [5, 10, 15, 20, 25];

// Autocomplete search input for player selection
const PlayerAutocomplete = ({ options, value, onChange, placeholder, disabled }) => {
  const [query, setQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filtered = query.length > 0
    ? options.filter(o => o.toLowerCase().includes(query.toLowerCase()))
    : [];

  const noMatch = query.length > 1 && filtered.length === 0;

  return (
    <div ref={ref} className="relative">
      {value ? (
        <div className="flex items-center justify-between px-4 py-3 bg-red-500/10 border-2 border-red-500 rounded-lg">
          <div className="flex items-center gap-2">
            <CheckCircle size={16} className="text-red-400" />
            <span className="text-white font-medium">{value}</span>
          </div>
          {!disabled && (
            <button onClick={() => { onChange(""); setQuery(""); }} className="text-slate-400 hover:text-white text-xs px-2 py-1 bg-slate-700 rounded">
              Change
            </button>
          )}
        </div>
      ) : (
        <div className="relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <input
            type="text"
            value={query}
            onChange={(e) => { setQuery(e.target.value); setIsOpen(true); }}
            onFocus={() => setIsOpen(true)}
            placeholder={placeholder || "Type to search..."}
            disabled={disabled}
            className="w-full pl-10 pr-4 py-3 bg-slate-900 border border-slate-700 rounded-lg text-white text-sm placeholder-slate-500 focus:border-red-500 focus:outline-none disabled:opacity-50"
            data-testid="player-search-input"
          />
          {isOpen && query.length > 0 && (
            <div className="absolute z-20 top-full mt-1 w-full bg-slate-800 border border-slate-600 rounded-lg shadow-xl max-h-48 overflow-y-auto">
              {noMatch ? (
                <div className="px-4 py-3 text-sm text-red-400 flex items-center gap-2">
                  <XCircle size={14} /> No player found matching "{query}"
                </div>
              ) : (
                filtered.slice(0, 15).map(opt => (
                  <button
                    key={opt}
                    onClick={() => { onChange(opt); setQuery(""); setIsOpen(false); }}
                    className="w-full text-left px-4 py-2 text-sm text-slate-300 hover:bg-slate-700 hover:text-white transition-colors"
                  >
                    {opt}
                  </button>
                ))
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Poll results display - only shows options that have votes
const PollResults = ({ pollData, userAnswer }) => {
  if (!pollData || pollData.total_votes === 0) return null;

  // Filter to only options with at least 1 vote, sorted by votes desc
  const votedOptions = Object.entries(pollData.votes || {})
    .filter(([, count]) => count > 0)
    .sort(([, a], [, b]) => b - a);

  if (votedOptions.length === 0) return null;

  return (
    <div className="mt-3 space-y-1.5">
      <p className="text-slate-500 text-xs flex items-center gap-1">
        <Users size={10} /> {pollData.total_votes} voted
      </p>
      {votedOptions.map(([option, count]) => {
        const pct = pollData.percentages?.[option] || 0;
        const isUserPick = userAnswer === option;
        return (
          <div key={option} className="relative rounded-lg overflow-hidden" data-testid={`poll-bar-${option}`}>
            <div
              className={`absolute inset-0 ${isUserPick ? "bg-red-500/20" : "bg-slate-700/40"}`}
              style={{ width: `${pct}%`, transition: "width 0.5s ease" }}
            />
            <div className="relative px-3 py-1.5 flex items-center justify-between">
              <span className={`text-xs ${isUserPick ? "text-red-400 font-medium" : "text-slate-400"}`}>
                {isUserPick && <CheckCircle size={10} className="inline mr-1" />}
                {option}
              </span>
              <span className={`text-xs font-mono ${isUserPick ? "text-red-400 font-bold" : "text-slate-500"}`}>
                {pct}%
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default function PredictorPage() {
  const { user, dbUser } = useAuth();
  const { winnersData, showCelebration, dismissCelebration } = useCelebration("predictor");
  const { showInterstitial } = useInterstitialAd();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState("upcoming");

  const [upcomingData, setUpcomingData] = useState({ matches: [], questions_by_match: {}, user_answers_by_match: {}, polls: {} });
  const [historyData, setHistoryData] = useState({ predictions: [], stats: { total: 0, correct: 0, accuracy: 0, total_points: 0 } });
  const [allMatches, setAllMatches] = useState([]);

  const [expandedMatch, setExpandedMatch] = useState(null);
  const [localAnswers, setLocalAnswers] = useState({});
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");

  // Admin State
  const [adminMatchId, setAdminMatchId] = useState("");
  const [adminMatchOptions, setAdminMatchOptions] = useState(null);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [adminQuestions, setAdminQuestions] = useState([]);
  const [creatingQuestions, setCreatingQuestions] = useState(false);
  const [existingQuestions, setExistingQuestions] = useState([]);
  const [isBonusMode, setIsBonusMode] = useState(false); // Bonus questions (no match required)
  // Grading State
  const [gradingMatchId, setGradingMatchId] = useState("");
  const [gradingData, setGradingData] = useState(null);
  const [loadingGrading, setLoadingGrading] = useState(false);
  const [gradingAnswers, setGradingAnswers] = useState({});
  const [grading, setGrading] = useState(false);

  // Bonus Questions Grading State
  const [bonusQuestions, setBonusQuestions] = useState([]);
  const [bonusLoading, setBonusLoading] = useState(false);
  const [bonusGradingAnswers, setBonusGradingAnswers] = useState({});
  const [bonusGrading, setBonusGrading] = useState(false);

  // Cap Predictions State (Top 3 Ranked)
  const [capData, setCapData] = useState(null);
  const [capLoading, setCapLoading] = useState(false);
  const [capPicks, setCapPicks] = useState({});  // { category: [name1, name2, name3] }
  const [capSubmitting, setCapSubmitting] = useState("");
  const [capSearches, setCapSearches] = useState({});  // { category: "search text" }
  // Predictor leaderboard
  const [predLeaderboard, setPredLeaderboard] = useState(null);
  // Native platform detection
  const [isNative, setIsNative] = useState(false);
  useEffect(() => {
    import("@capacitor/core").then(({ Capacitor }) => {
      setIsNative(Capacitor.isNativePlatform());
    }).catch(() => {});
  }, []);
  // Tournament Stats (Admin)
  const [capStats, setCapStats] = useState(null);
  const [capStatsLoading, setCapStatsLoading] = useState(false);

  const isAdmin = dbUser?.is_admin || false;
  const isPredictorAdmin = dbUser?.is_predictor_admin || false;
  const canManagePredictions = isAdmin || isPredictorAdmin;

  const fetchData = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const token = await user.getIdToken(true);
      const headers = { Authorization: `Bearer ${token}` };
      const requests = [
        axios.get(`${API}/predictions/upcoming`, { headers }),
        axios.get(`${API}/predictions/my-history`, { headers })
      ];
      if (canManagePredictions) {
        requests.push(axios.get(`${API}/matches`, { headers }));
      }
      const responses = await Promise.all(requests);

      setUpcomingData(responses[0].data || { matches: [], questions_by_match: {}, user_answers_by_match: {}, polls: {} });
      setHistoryData(responses[1].data || { predictions: [], stats: { total: 0, correct: 0, accuracy: 0, total_points: 0 } });

      if (canManagePredictions && responses[2]) {
        const matchesData = responses[2].data?.matches || [];
        setAllMatches(Array.isArray(matchesData) ? matchesData : []);
      }

      const existingAnswers = {};
      Object.entries(responses[0].data?.user_answers_by_match || {}).forEach(([, answers]) => {
        Object.entries(answers || {}).forEach(([questionId, answer]) => {
          existingAnswers[questionId] = answer;
        });
      });
      setLocalAnswers(existingAnswers);
    } catch (e) {
      console.error("Failed to fetch predictions data:", e);
    } finally {
      setLoading(false);
    }
  }, [user, canManagePredictions]);

  useEffect(() => {
    if (user) fetchData();
  }, [user, fetchData]);

  const handleAdminMatchSelect = async (matchId) => {
    setAdminMatchId(matchId);
    setAdminMatchOptions(null);
    setAdminQuestions([]);
    setExistingQuestions([]);
    if (!matchId) return;

    setLoadingOptions(true);
    try {
      const token = await user.getIdToken(true);
      const headers = { Authorization: `Bearer ${token}` };
      const [optionsRes, existingRes] = await Promise.all([
        axios.get(`${API}/predictions/match-options/${matchId}`, { headers }),
        axios.get(`${API}/admin/predictions/match/${matchId}`, { headers }),
      ]);
      setAdminMatchOptions(optionsRes.data);
      setExistingQuestions(existingRes.data?.questions || []);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to load match options");
    } finally {
      setLoadingOptions(false);
    }
  };

  const addQuestion = (qType) => {
    // For bonus mode, we don't need adminMatchOptions
    if (!isBonusMode && !adminMatchOptions) return;
    
    let options = [];
    if (qType === "yes_no") {
      options = ["Yes", "No"];
    } else if (qType === "over_under") {
      options = []; // Will be generated from threshold
    } else if (qType === "range" || qType === "bonus" || qType === "custom") {
      options = []; // Admin types these manually
    } else if (!isBonusMode) {
      options = [...(adminMatchOptions.options_by_type[qType] || [])];
    }
    setAdminQuestions(prev => [...prev, {
      question_type: qType,
      question_text: "",
      options,
      points: 10,
      order: existingQuestions.length + prev.length + 1,
      threshold: null, // For over_under questions
      is_bonus: isBonusMode,
    }]);
  };

  const removeQuestion = (index) => setAdminQuestions(prev => prev.filter((_, i) => i !== index));

  const updateQuestionPoints = (index, points) => {
    setAdminQuestions(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], points };
      return updated;
    });
  };

  const createQuestions = async () => {
    // For bonus mode, match_id is optional
    if (!isBonusMode && !adminMatchId) { 
      setError("Select a match or enable Bonus Mode for non-match questions"); 
      return; 
    }
    if (adminQuestions.length === 0) { 
      setError("Add at least one question"); 
      return; 
    }
    
    setCreatingQuestions(true); setError("");
    try {
      const token = await user.getIdToken(true);
      await axios.post(`${API}/admin/predictions/create`, {
        match_id: isBonusMode ? null : adminMatchId,
        is_bonus: isBonusMode,
        questions: adminQuestions.map(q => {
          // For over_under, generate options from threshold
          let options = q.options;
          if (q.question_type === "over_under" && q.threshold) {
            options = [`Under ${q.threshold}`, `Over ${q.threshold}`];
          }
          return {
            question_type: q.question_type,
            question_text: q.question_text || "",
            options,
            points: q.points,
            order: q.order || 0,
            is_bonus: q.is_bonus || isBonusMode,
          };
        })
      }, { headers: { Authorization: `Bearer ${token}` } });
      setSuccess(`Created ${adminQuestions.length} question(s)!`);
      setAdminQuestions([]);
      if (!isBonusMode && adminMatchId) {
        await handleAdminMatchSelect(adminMatchId);
      }
      await fetchData();
      setTimeout(() => setSuccess(""), 3000);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to create questions");
    } finally {
      setCreatingQuestions(false);
    }
  };

  const deleteQuestion = async (questionId) => {
    try {
      const token = await user.getIdToken(true);
      await axios.delete(`${API}/admin/predictions/question/${questionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setExistingQuestions(prev => prev.filter(q => q.id !== questionId));
      setSuccess("Question deleted");
      setTimeout(() => setSuccess(""), 2000);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to delete question");
    }
  };

  const toggleMatchLock = async (matchId) => {
    try {
      const token = await user.getIdToken(true);
      const res = await axios.post(`${API}/admin/predictions/toggle-lock/${matchId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuccess(res.data.message);
      await fetchData();
      setTimeout(() => setSuccess(""), 3000);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to toggle lock");
    }
  };

  const handleGradingMatchSelect = async (matchId) => {
    setGradingMatchId(matchId);
    setGradingData(null);
    setGradingAnswers({});
    if (!matchId) return;
    setLoadingGrading(true);
    try {
      const token = await user.getIdToken(true);
      const res = await axios.get(`${API}/admin/predictions/auto-answers/${matchId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGradingData(res.data);
      const preAnswers = {};
      for (const [qid, info] of Object.entries(res.data.auto_answers || {})) {
        preAnswers[qid] = { answer: info.answer || "", answers: info.answers || [], is_voided: info.is_voided || false };
      }
      setGradingAnswers(preAnswers);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to load grading data");
    } finally {
      setLoadingGrading(false);
    }
  };

  const gradeMatch = async () => {
    if (!gradingMatchId || Object.keys(gradingAnswers).length === 0) { setError("Select a match and set correct answers"); return; }
    setGrading(true); setError("");
    try {
      const token = await user.getIdToken(true);
      const result = await axios.post(`${API}/admin/predictions/grade/${gradingMatchId}`,
        { answers: gradingAnswers },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const d = result.data;
      setSuccess(`Graded ${d.total_graded} predictions! ${d.correct} correct, ${d.total_points_awarded} pts awarded.${d.voided_questions > 0 ? ` ${d.voided_questions} voided.` : ""}`);
      setGradingAnswers({});
      setGradingMatchId("");
      setGradingData(null);
      await fetchData();
      setTimeout(() => setSuccess(""), 5000);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to grade predictions");
    } finally {
      setGrading(false);
    }
  };

  const handleAnswerChange = (questionId, answer) => {
    setLocalAnswers(prev => ({ ...prev, [questionId]: answer }));
  };

  const fetchCapData = useCallback(async () => {
    if (!user) return;
    setCapLoading(true);
    try {
      const token = await user.getIdToken(true);
      const res = await axios.get(`${API}/cap-predictions/options`, { headers: { Authorization: `Bearer ${token}` } });
      setCapData(res.data);
      // Populate existing picks
      const existingPicks = {};
      for (const cat of res.data.categories || []) {
        existingPicks[cat] = res.data.my_predictions?.[cat] || ["", "", ""];
      }
      setCapPicks(existingPicks);
    } catch (e) {
      console.error("Failed to fetch cap predictions:", e);
    } finally {
      setCapLoading(false);
    }
  }, [user]);

  const fetchCapStats = useCallback(async () => {
    if (!user || !isAdmin) return;
    setCapStatsLoading(true);
    try {
      const token = await user.getIdToken(true);
      const res = await axios.get(`${API}/admin/cap-stats`, { headers: { Authorization: `Bearer ${token}` } });
      setCapStats(res.data);
    } catch (e) {
      console.error("Failed to fetch cap stats:", e);
    } finally {
      setCapStatsLoading(false);
    }
  }, [user, isAdmin]);

  const submitCapPrediction = async (category) => {
    const picks = capPicks[category] || [];
    if (picks.filter(Boolean).length !== 3) {
      setError("Please select all 3 positions");
      return;
    }
    setCapSubmitting(category);
    setError("");
    try {
      const token = await user.getIdToken(true);
      await axios.post(`${API}/cap-predictions/submit`, { category, picks }, { headers: { Authorization: `Bearer ${token}` } });
      setSuccess(`${capData?.labels?.[category] || category} prediction saved!`);
      await fetchCapData();
      setTimeout(() => setSuccess(""), 3000);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to save prediction");
    } finally {
      setCapSubmitting("");
    }
  };

  const fetchPredLeaderboard = useCallback(async () => {
    try {
      const token = await user.getIdToken(true);
      const res = await axios.get(`${API}/predictions/leaderboard`, { headers: { Authorization: `Bearer ${token}` } });
      setPredLeaderboard(res.data.leaderboard || []);
    } catch {
      setPredLeaderboard([]);
    }
  }, [user]);

  const submitPredictions = async (matchId) => {
    if (!user) return;
    const questions = upcomingData.questions_by_match[matchId] || [];
    const predictions = questions
      .filter(q => localAnswers[q.id])
      .map(q => ({ question_id: q.id, answer: localAnswers[q.id] }));
    if (predictions.length === 0) { setError("Please answer at least one question before submitting."); return; }
    setSubmitting(true); setError("");
    try {
      const token = await user.getIdToken(true);
      await axios.post(`${API}/predictions/submit/${matchId}`, predictions, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuccess("Predictions submitted!");
      hapticSuccess();
      showInterstitial();
      await fetchData();
      setTimeout(() => setSuccess(""), 3000);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to submit predictions");
    } finally {
      setSubmitting(false);
    }
  };

  if (!user) {
    return (
      <div className="text-center py-12" data-testid="predictor-login-prompt">
        <Target className="mx-auto text-slate-600 mb-4" size={48} />
        <p className="text-slate-400 mb-4">Please login to make predictions</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="animate-spin text-emerald-400" size={48} />
      </div>
    );
  }

  const matchesWithQuestions = Array.isArray(upcomingData.matches)
    ? upcomingData.matches.filter(m => (upcomingData.questions_by_match[m.match_id] || []).length > 0)
    : [];

  const allMatchesForAdmin = Array.isArray(allMatches)
    ? [...allMatches]
        .filter(m => m.status === "upcoming" || m.status === "completed" || m.status === "result")
        .sort((a, b) => (a.match_number || 0) - (b.match_number || 0))
    : [];

  const completedMatchesForGrading = Array.isArray(allMatches)
    ? allMatches.filter(m => m.status === "completed" || m.status === "result")
    : [];

  return (
    <div>
    <div className="space-y-6 pb-24" data-testid="predictor-page">
      {showCelebration && winnersData && (
        <CelebrationOverlay winnersData={winnersData} pageType="predictor" onDismiss={dismissCelebration} />
      )}
      {/* Header */}
      <div className="text-center">
        <h1 className="text-2xl sm:text-3xl font-bold text-white flex items-center justify-center gap-3">
          <Target className="text-red-400" size={32} />
          Match Predictor
        </h1>
        <p className="text-slate-400 mt-2">Predict match outcomes and earn bonus points!</p>
      </div>

      {/* Messages */}
      {error && (
        <div className="bg-red-500/20 border border-red-500/50 rounded-xl p-4 flex items-center gap-3" data-testid="predictor-error">
          <AlertTriangle className="text-red-400 flex-shrink-0" size={20} />
          <span className="text-red-400 text-sm flex-1">{error}</span>
          <button onClick={() => setError("")} className="text-red-400 hover:text-red-300 text-lg leading-none">&times;</button>
        </div>
      )}
      {success && (
        <div className="bg-emerald-500 text-white rounded-xl p-4 flex items-center gap-3" data-testid="predictor-success">
          <CheckCircle size={20} />
          <span className="font-medium text-sm">{success}</span>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4 text-center">
          <Trophy className="mx-auto text-red-400 mb-2" size={24} />
          <p className="text-2xl font-bold text-white" data-testid="pred-total-points">{historyData.stats.total_points}</p>
          <p className="text-xs text-slate-400">Predictor Pts</p>
        </div>
        <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4 text-center">
          <TrendingUp className="mx-auto text-emerald-400 mb-2" size={24} />
          <p className="text-2xl font-bold text-white" data-testid="pred-accuracy">{historyData.stats.accuracy}%</p>
          <p className="text-xs text-slate-400">Accuracy</p>
        </div>
        <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4 text-center">
          <CheckCircle className="mx-auto text-green-400 mb-2" size={24} />
          <p className="text-2xl font-bold text-white" data-testid="pred-correct">{historyData.stats.correct}</p>
          <p className="text-xs text-slate-400">Correct</p>
        </div>
        <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4 text-center">
          <Zap className="mx-auto text-purple-400 mb-2" size={24} />
          <p className="text-2xl font-bold text-white" data-testid="pred-total">{historyData.stats.total}</p>
          <p className="text-xs text-slate-400">Total Predictions</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-1 sm:gap-2 bg-slate-800/50 p-1 rounded-xl">
        {["upcoming", "caps", "rankings", "history", ...(canManagePredictions ? ["admin"] : [])].map(tab => (
          <button
            key={tab}
            onClick={() => {
              setActiveTab(tab);
              if (tab === "caps" && !capData) fetchCapData();
              if (tab === "rankings" && !predLeaderboard) fetchPredLeaderboard();
            }}
            data-testid={`tab-${tab}`}
            className={`flex-1 min-w-fit py-2 px-2 sm:px-4 rounded-lg font-medium transition-all flex items-center justify-center gap-1 sm:gap-2 text-xs sm:text-sm ${
              activeTab === tab
                ? tab === "admin" ? "bg-orange-500 text-white" : tab === "caps" ? "bg-gradient-to-r from-orange-500 to-purple-500 text-white" : tab === "rankings" ? "bg-cyan-500 text-white" : "bg-emerald-500 text-white"
                : "text-slate-400 hover:text-white"
            }`}
          >
            {tab === "upcoming" && <><Clock size={14} className="sm:w-4 sm:h-4" /> <span className="hidden xs:inline">Upcoming</span><span className="xs:hidden">Live</span> {matchesWithQuestions.length > 0 && <span className="bg-white/20 px-1.5 py-0.5 rounded-full text-[10px] sm:text-xs">{matchesWithQuestions.length}</span>}</>}
            {tab === "caps" && <><Award size={14} className="sm:w-4 sm:h-4" /> Caps</>}
            {tab === "rankings" && <><Trophy size={14} className="sm:w-4 sm:h-4" /> <span className="hidden sm:inline">Rankings</span><span className="sm:hidden">Rank</span></>}
            {tab === "history" && <><Award size={14} className="sm:w-4 sm:h-4" /> <span className="hidden sm:inline">History</span><span className="sm:hidden">Past</span></>}
            {tab === "admin" && <><Settings size={14} className="sm:w-4 sm:h-4" /> Admin</>}
          </button>
        ))}
      </div>

      {/* ==================== UPCOMING TAB ==================== */}
      {activeTab === "upcoming" && (
        <div className="space-y-4">
          {/* Tournament-Wide Bonus Questions Section */}
          {upcomingData.bonus_questions && upcomingData.bonus_questions.filter(q => !q.is_graded).length > 0 && (
            <div className="bg-gradient-to-br from-amber-500/10 to-orange-500/10 border border-amber-500/30 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-3">
                <Star className="w-5 h-5 text-amber-400" />
                <h3 className="text-amber-400 font-bold">Tournament Bonus Questions</h3>
                <span className="text-xs bg-amber-500/20 text-amber-300 px-2 py-0.5 rounded-full">Free for all!</span>
              </div>
              <div className="space-y-3">
                {upcomingData.bonus_questions.filter(q => !q.is_graded).map((q) => {
                  const pollData = upcomingData.polls?.[q.id];
                  const selectedOption = q.id in localAnswers ? localAnswers[q.id] : (upcomingData.bonus_user_answers?.[q.id] || "");
                  const isShortList = q.options.length <= 4;
                  const isLocked = q.is_locked;
                  const hasExistingAnswer = upcomingData.bonus_user_answers?.[q.id];
                  return (
                    <div key={q.id} className={`rounded-xl p-4 bg-slate-800/50 border ${isLocked ? "border-red-500/30" : "border-amber-500/20"}`} data-testid={`tournament-bonus-${q.id}`}>
                      <div className="flex items-start gap-3 mb-3">
                        <Star className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                        <div className="flex-1">
                          <p className="text-white font-medium">{q.question_text}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <span className="text-amber-400 text-xs">+{q.points} bonus pts</span>
                            {isLocked && <span className="text-xs text-red-400 flex items-center gap-1"><Lock size={10} /> Locked</span>}
                            {hasExistingAnswer && !isLocked && <span className="text-xs text-emerald-400">(Your answer: {hasExistingAnswer})</span>}
                          </div>
                        </div>
                      </div>
                      {isLocked ? (
                        <div className="px-4 py-3 rounded-lg bg-slate-700/50 text-slate-400 text-sm">
                          {hasExistingAnswer ? `Your answer: ${hasExistingAnswer}` : "You didn't answer before it was locked"}
                        </div>
                      ) : isShortList ? (
                        <div className="space-y-2">
                          {q.options.map(option => {
                            const isSelected = selectedOption === option;
                            return (
                              <button
                                key={option}
                                onClick={() => handleAnswerChange(q.id, option)}
                                className={`w-full px-4 py-3 rounded-lg text-left transition-all ${
                                  isSelected ? "border-2 border-amber-500 bg-amber-500/10" : "border border-slate-700 hover:border-slate-500"
                                }`}
                                data-testid={`bonus-option-${q.id}-${option}`}
                              >
                                <div className="flex items-center gap-2">
                                  {isSelected && <CheckCircle size={16} className="text-amber-400" />}
                                  <span className={isSelected ? "text-white font-medium" : "text-slate-300"}>{option}</span>
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      ) : (
                        <PlayerAutocomplete
                          options={q.options}
                          value={selectedOption || ""}
                          onChange={(val) => handleAnswerChange(q.id, val)}
                          placeholder="Search..."
                        />
                      )}
                      <PollResults pollData={pollData} userAnswer={selectedOption} />
                    </div>
                  );
                })}
              </div>
              {/* Submit Bonus Questions Button - Show if any bonus question has a local change and is not locked */}
              {upcomingData.bonus_questions.filter(q => !q.is_graded).some(q => !q.is_locked && q.id in localAnswers && localAnswers[q.id] !== upcomingData.bonus_user_answers?.[q.id]) && (
                <button
                  onClick={async () => {
                    const bonusQuestions = upcomingData.bonus_questions || [];
                    const predictions = bonusQuestions
                      .filter(q => !q.is_locked && localAnswers[q.id] && localAnswers[q.id] !== upcomingData.bonus_user_answers?.[q.id])
                      .map(q => ({ question_id: q.id, answer: localAnswers[q.id] }));
                    if (predictions.length === 0) return;
                    setSubmitting(true);
                    try {
                      const token = await user.getIdToken(true);
                      await axios.post(`${API}/predictions/submit-bonus`, predictions, { headers: { Authorization: `Bearer ${token}` } });
                      setSuccess("Bonus predictions submitted!");
                      hapticSuccess();
                      await fetchData();
                      setTimeout(() => setSuccess(""), 3000);
                    } catch (e) {
                      setError(e.response?.data?.detail || "Failed to submit");
                    } finally {
                      setSubmitting(false);
                    }
                  }}
                  disabled={submitting}
                  className="mt-4 w-full py-3 bg-amber-500 text-white rounded-xl font-semibold hover:bg-amber-600 disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="submit-bonus-predictions"
                >
                  {submitting ? <Loader2 className="animate-spin" size={18} /> : <ArrowRight size={18} />}
                  Submit Bonus Predictions
                </button>
              )}
            </div>
          )}

          {matchesWithQuestions.length === 0 && (!upcomingData.bonus_questions || upcomingData.bonus_questions.length === 0) ? (
            <div className="text-center py-12 bg-slate-800/50 rounded-xl border border-slate-700">
              <Calendar className="mx-auto text-slate-600 mb-4" size={48} />
              <p className="text-slate-400">No prediction questions available yet.</p>
              <p className="text-slate-500 text-sm mt-2">Check back before the next match!</p>
            </div>
          ) : matchesWithQuestions.length > 0 ? (
            matchesWithQuestions.map(match => {
              const allQuestions = upcomingData.questions_by_match[match.match_id] || [];
              const matchQuestions = allQuestions.filter(q => q.question_type !== "bonus");
              const bonusQuestions = allQuestions.filter(q => q.question_type === "bonus");
              const userAnswers = upcomingData.user_answers_by_match[match.match_id] || {};
              const polls = upcomingData.polls || {};
              const isExpanded = expandedMatch === match.match_id;
              const answeredCount = allQuestions.filter(q => localAnswers[q.id] || userAnswers[q.id]).length;
              const isLocked = match.is_locked;

              // Points multiplier (tier perk) — still applied to scoring display.
              const multiplier = upcomingData.tier_info?.multiplier || 1.0;

              return (
                <div key={match.match_id} className="bg-slate-800/50 border border-slate-700 rounded-xl overflow-hidden" data-testid={`match-card-${match.match_id}`}>
                  <button
                    onClick={() => setExpandedMatch(isExpanded ? null : match.match_id)}
                    className="w-full px-4 py-4 flex items-center justify-between hover:bg-slate-700/30 transition-all"
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${isLocked ? "bg-slate-600/50" : "bg-red-500/20"}`}>
                        {isLocked ? <Lock className="text-slate-400" size={20} /> : <Target className="text-red-400" size={20} />}
                      </div>
                      <div className="text-left">
                        <p className="text-white font-semibold">{getDisplayName(match.team1)} vs {getDisplayName(match.team2)}</p>
                        <p className="text-slate-400 text-sm">
                          {match.date} {match.time_local && `\u2022 ${match.time_local}`}
                          {isLocked && <span className="text-red-400 ml-2 text-xs font-medium">LOCKED</span>}
                          {multiplier > 1 && <span className="text-amber-400 ml-2 text-xs font-medium">{multiplier}x pts</span>}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        answeredCount === allQuestions.length
                          ? "bg-emerald-500/20 text-emerald-400"
                          : answeredCount > 0 ? "bg-amber-500/20 text-amber-400" : "bg-slate-600 text-slate-400"
                      }`}>
                        {answeredCount}/{allQuestions.length}
                      </span>
                      {isExpanded ? <ChevronUp size={20} className="text-slate-400" /> : <ChevronDown size={20} className="text-slate-400" />}
                    </div>
                  </button>

                  {isExpanded && (
                    <div className="px-4 pb-4 space-y-4 border-t border-slate-700/50 pt-4">
                      {isLocked && (
                        <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 flex items-center gap-2">
                          <Lock size={16} className="text-red-400" />
                          <span className="text-red-400 text-sm">Predictions are locked — match has started or is completed.</span>
                        </div>
                      )}

                      {/* Match Questions with tier gating */}
                      {matchQuestions.map((q, idx) => {
                        const pollData = polls[q.id];
                        const selectedOption = q.id in localAnswers ? localAnswers[q.id] : (userAnswers[q.id] || "");
                        const qOrder = q.order || idx + 1;
                        // AdMob disabled — all predictor questions are free for everyone (Issue E).
                        const isAccessible = true;
                        const isShortList = q.question_type === "winner" || q.question_type === "yes_no" || q.question_type === "over_under";

                        return (
                          <div key={q.id} className={`rounded-xl p-4 transition-all ${isAccessible ? "bg-slate-900/50" : "bg-slate-900/30 border border-slate-700/30"}`} data-testid={`question-${q.id}`}>
                            <div className="flex items-start gap-3 mb-3">
                              <span className={`w-6 h-6 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 ${
                                isAccessible ? "bg-red-500/20 text-red-400" : "bg-slate-700 text-slate-500"
                              }`}>
                                {isAccessible ? qOrder : <Lock size={12} />}
                              </span>
                              <div className="flex-1">
                                <p className={`font-medium ${isAccessible ? "text-white" : "text-slate-500"}`}>{q.question_text}</p>
                                <div className="flex items-center gap-2 mt-0.5">
                                  <span className={`text-xs ${isAccessible ? "text-red-400" : "text-slate-600"}`}>
                                    +{multiplier > 1 ? `${q.points} (x${multiplier} = ${Math.round(q.points * multiplier)})` : q.points} pts
                                  </span>
                                  {q.question_type !== "winner" && q.question_type !== "custom" && (
                                    <span className="text-xs text-slate-600 bg-slate-800 px-1.5 py-0.5 rounded">
                                      {QUESTION_TYPE_LABELS[q.question_type] || q.question_type}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>

                            {/* Locked overlay removed — AdMob disabled, all questions free (Issue E) */}

                            {/* Unlocked question options */}
                            {isAccessible && (
                              <>
                                {isShortList ? (
                                  <div className="space-y-2 mt-3">
                                    {q.options.map(option => {
                                      const isSelected = selectedOption === option;
                                      return (
                                        <button
                                          key={option}
                                          onClick={() => !isLocked && handleAnswerChange(q.id, option)}
                                          disabled={isLocked}
                                          className={`w-full px-4 py-3 rounded-lg text-left transition-all ${
                                            isSelected
                                              ? "border-2 border-red-500 bg-red-500/10"
                                              : "border border-slate-700 hover:border-slate-500"
                                          } ${isLocked ? "opacity-60 cursor-not-allowed" : ""}`}
                                          data-testid={`option-${q.id}-${option}`}
                                        >
                                          <div className="flex items-center gap-2">
                                            {isSelected && <CheckCircle size={16} className="text-red-400" />}
                                            <span className={isSelected ? "text-white font-medium" : "text-slate-300"}>{option}</span>
                                          </div>
                                        </button>
                                      );
                                    })}
                                  </div>
                                ) : q.question_type === "range" ? (
                                  <div className="grid grid-cols-2 gap-2 mt-3">
                                    {q.options.map(option => {
                                      const isSelected = selectedOption === option;
                                      return (
                                        <button
                                          key={option}
                                          onClick={() => !isLocked && handleAnswerChange(q.id, option)}
                                          disabled={isLocked}
                                          className={`px-3 py-2.5 rounded-lg text-center transition-all text-sm ${
                                            isSelected
                                              ? "border-2 border-red-500 bg-red-500/10 text-white font-medium"
                                              : "border border-slate-700 text-slate-300 hover:border-slate-500"
                                          } ${isLocked ? "opacity-60 cursor-not-allowed" : ""}`}
                                          data-testid={`option-${q.id}-${option}`}
                                        >
                                          {option}
                                        </button>
                                      );
                                    })}
                                  </div>
                                ) : (
                                  <div className="mt-3">
                                    <PlayerAutocomplete
                                      options={q.options}
                                      value={selectedOption || ""}
                                      onChange={(val) => !isLocked && handleAnswerChange(q.id, val)}
                                      placeholder={`Search player name...`}
                                      disabled={isLocked}
                                    />
                                  </div>
                                )}

                                <PollResults pollData={pollData} userAnswer={selectedOption} />
                              </>
                            )}
                          </div>
                        );
                      })}

                      {/* Bonus Questions Section */}
                      {bonusQuestions.length > 0 && (
                        <div className="space-y-3">
                          <div className="flex items-center gap-2 mt-2">
                            <Star className="w-4 h-4 text-amber-400" />
                            <h3 className="text-amber-400 font-semibold text-sm">Bonus Predictions</h3>
                            <span className="text-xs text-slate-500">Free for everyone!</span>
                          </div>
                          {bonusQuestions.map((q, idx) => {
                            const pollData = polls[q.id];
                            const selectedOption = q.id in localAnswers ? localAnswers[q.id] : (userAnswers[q.id] || "");
                            const isShortList = q.options.length <= 4;
                            return (
                              <div key={q.id} className="rounded-xl p-4 bg-amber-500/5 border border-amber-500/20" data-testid={`bonus-question-${q.id}`}>
                                <div className="flex items-start gap-3 mb-3">
                                  <Star className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                                  <div className="flex-1">
                                    <p className="text-white font-medium">{q.question_text}</p>
                                    <span className="text-amber-400 text-xs">+{q.points} bonus pts</span>
                                  </div>
                                </div>
                                {isShortList ? (
                                  <div className="space-y-2">
                                    {q.options.map(option => {
                                      const isSelected = selectedOption === option;
                                      return (
                                        <button
                                          key={option}
                                          onClick={() => !isLocked && handleAnswerChange(q.id, option)}
                                          disabled={isLocked}
                                          className={`w-full px-4 py-3 rounded-lg text-left transition-all ${
                                            isSelected ? "border-2 border-amber-500 bg-amber-500/10" : "border border-slate-700 hover:border-slate-500"
                                          } ${isLocked ? "opacity-60 cursor-not-allowed" : ""}`}
                                          data-testid={`option-${q.id}-${option}`}
                                        >
                                          <div className="flex items-center gap-2">
                                            {isSelected && <CheckCircle size={16} className="text-amber-400" />}
                                            <span className={isSelected ? "text-white font-medium" : "text-slate-300"}>{option}</span>
                                          </div>
                                        </button>
                                      );
                                    })}
                                  </div>
                                ) : (
                                  <PlayerAutocomplete
                                    options={q.options}
                                    value={selectedOption || ""}
                                    onChange={(val) => !isLocked && handleAnswerChange(q.id, val)}
                                    placeholder="Search..."
                                    disabled={isLocked}
                                  />
                                )}
                                <PollResults pollData={pollData} userAnswer={selectedOption} />
                              </div>
                            );
                          })}
                        </div>
                      )}

                      {!isLocked && (
                        <button
                          onClick={() => submitPredictions(match.match_id)}
                          disabled={submitting || answeredCount === 0}
                          className="w-full py-3 bg-red-500 text-white rounded-xl font-semibold hover:bg-red-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                          data-testid={`submit-${match.match_id}`}
                        >
                          {submitting ? (
                            <><Loader2 className="animate-spin" size={18} /> Submitting...</>
                          ) : (
                            <><ArrowRight size={18} /> Submit Predictions</>
                          )}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })
          ) : null}
        </div>
      )}

      {/* ==================== CAPS PREDICTION TAB ==================== */}
      {activeTab === "caps" && (
        <div className="space-y-5" data-testid="caps-tab">
          {/* Intro */}
          <div className="bg-gradient-to-r from-orange-500/10 to-purple-500/10 border border-orange-500/30 rounded-xl p-4">
            <h3 className="text-white font-semibold flex items-center gap-2 mb-1">
              <Award size={18} className="text-orange-400" /> Tournament Cap Predictions
            </h3>
            <p className="text-slate-400 text-sm">Predict the <strong>Top 3</strong> in each category. Rank them in order — the better your order, the more points you win!</p>
            <div className="flex gap-3 mt-2 text-xs">
              <span className="bg-emerald-500/20 text-emerald-400 px-2 py-1 rounded-lg">All 3 correct order = <strong>100 pts</strong></span>
              <span className="bg-cyan-500/20 text-cyan-400 px-2 py-1 rounded-lg">2 right = <strong>50 pts</strong></span>
              <span className="bg-slate-600/50 text-slate-300 px-2 py-1 rounded-lg">1 right = <strong>25 pts</strong></span>
            </div>
            <p className="text-slate-500 text-xs mt-2">
              {capData?.is_locked
                ? <span className="text-red-400 font-medium flex items-center gap-1"><Lock size={12} /> Predictions are locked</span>
                : "Edit anytime until the final match starts."
              }
            </p>
            <button
              onClick={() => {
                const msg = `Think you know cricket? Predict the Top 3 run scorers, wicket takers, and more on APL Fantasy!\n\nJoin now and prove your cricket IQ:\n${window.location.origin}`;
                window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, '_blank');
              }}
              className="mt-3 flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-500 text-white text-sm font-medium rounded-lg transition-all"
              data-testid="cap-share-whatsapp"
            >
              <Share2 size={15} /> Invite Friends to Predict
            </button>
          </div>

          {capLoading ? (
            <div className="flex justify-center py-8"><Loader2 className="animate-spin text-orange-400" size={32} /></div>
          ) : capData ? (
            <>
              {(capData.categories || []).map(cat => {
                const label = capData.labels?.[cat] || cat;
                const options = capData.options_by_category?.[cat] || [];
                const picks = capPicks[cat] || ["", "", ""];
                const search = capSearches[cat] || "";
                const poll = capData.polls?.[cat];
                const catColor = cat === "top_scorer" ? "orange" : cat === "top_wicket_taker" ? "purple" : cat === "top_catches" ? "cyan" : "emerald";
                const borderClass = `border-${catColor}-500/30`;
                const bgClass = `bg-${catColor}-500/20`;
                const textClass = `text-${catColor}-400`;
                const savedPicks = capData.my_predictions?.[cat] || [];
                const hasChanged = JSON.stringify(picks) !== JSON.stringify(savedPicks);
                const allFilled = picks.filter(Boolean).length === 3;

                return (
                  <div key={cat} className={`bg-slate-800/50 border rounded-xl p-4 ${
                    cat === "top_scorer" ? "border-orange-500/30" :
                    cat === "top_wicket_taker" ? "border-purple-500/30" :
                    cat === "top_catches" ? "border-cyan-500/30" :
                    "border-emerald-500/30"
                  }`} data-testid={`cap-category-${cat}`}>
                    <div className="flex items-center gap-2 mb-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                        cat === "top_scorer" ? "bg-orange-500/20" :
                        cat === "top_wicket_taker" ? "bg-purple-500/20" :
                        cat === "top_catches" ? "bg-cyan-500/20" :
                        "bg-emerald-500/20"
                      }`}>
                        <Trophy size={16} className={
                          cat === "top_scorer" ? "text-orange-400" :
                          cat === "top_wicket_taker" ? "text-purple-400" :
                          cat === "top_catches" ? "text-cyan-400" :
                          "text-emerald-400"
                        } />
                      </div>
                      <div>
                        <h4 className="text-white font-semibold text-sm">{label}</h4>
                        <p className="text-slate-500 text-xs">Rank your Top 3 in order</p>
                      </div>
                    </div>

                    {/* Top 3 Slots with drag */}
                    <div className="space-y-2 mb-3">
                      {[0, 1, 2].map(slot => (
                        <div
                          key={slot}
                          className={`flex items-center gap-2 p-2.5 rounded-lg border transition-all ${
                            picks[slot] ? "bg-slate-700/50 border-slate-600" : "bg-slate-900/50 border-slate-700/50 border-dashed"
                          }`}
                          draggable={!!picks[slot] && !capData.is_locked}
                          onDragStart={(e) => { e.dataTransfer.setData("text/plain", `${cat}:${slot}`); }}
                          onDragOver={(e) => e.preventDefault()}
                          onDrop={(e) => {
                            e.preventDefault();
                            const data = e.dataTransfer.getData("text/plain");
                            const [fromCat, fromSlotStr] = data.split(":");
                            if (fromCat !== cat) return;
                            const fromSlot = parseInt(fromSlotStr);
                            if (fromSlot === slot) return;
                            const newPicks = [...picks];
                            [newPicks[fromSlot], newPicks[slot]] = [newPicks[slot], newPicks[fromSlot]];
                            setCapPicks(prev => ({ ...prev, [cat]: newPicks }));
                          }}
                          data-testid={`cap-slot-${cat}-${slot}`}
                        >
                          <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                            slot === 0 ? "bg-yellow-500 text-black" :
                            slot === 1 ? "bg-slate-400 text-black" :
                            "bg-orange-700 text-white"
                          }`}>{slot + 1}</span>

                          {picks[slot] ? (
                            <div className="flex-1 flex items-center justify-between">
                              <span className="text-white text-sm font-medium">{picks[slot]}</span>
                              {!capData.is_locked && (
                                <button
                                  onClick={() => {
                                    const newPicks = [...picks];
                                    newPicks[slot] = "";
                                    setCapPicks(prev => ({ ...prev, [cat]: newPicks }));
                                  }}
                                  className="text-slate-500 hover:text-red-400 transition-colors p-1"
                                >
                                  <XCircle size={16} />
                                </button>
                              )}
                            </div>
                          ) : (
                            <span className="text-slate-600 text-sm italic">
                              {slot === 0 ? "1st place" : slot === 1 ? "2nd place" : "3rd place"}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>

                    {/* Search to add players */}
                    {!capData.is_locked && picks.filter(Boolean).length < 3 && (
                      <div className="relative mb-3">
                        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                        <input
                          type="text"
                          value={search}
                          onChange={(e) => setCapSearches(prev => ({ ...prev, [cat]: e.target.value }))}
                          placeholder={`Search player to add...`}
                          className="w-full pl-9 pr-3 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-white text-sm placeholder:text-slate-600 focus:outline-none focus:border-slate-500"
                          data-testid={`cap-search-${cat}`}
                        />
                        {search.length >= 2 && (
                          <div className="absolute z-10 mt-1 w-full max-h-40 overflow-y-auto bg-slate-800 border border-slate-700 rounded-lg shadow-xl">
                            {options
                              .filter(n => n.toLowerCase().includes(search.toLowerCase()) && !picks.includes(n))
                              .slice(0, 8)
                              .map(name => (
                                <button
                                  key={name}
                                  onClick={() => {
                                    const emptySlot = picks.indexOf("");
                                    if (emptySlot === -1) return;
                                    const newPicks = [...picks];
                                    newPicks[emptySlot] = name;
                                    setCapPicks(prev => ({ ...prev, [cat]: newPicks }));
                                    setCapSearches(prev => ({ ...prev, [cat]: "" }));
                                  }}
                                  className="w-full text-left px-3 py-2 text-sm text-slate-300 hover:bg-slate-700 hover:text-white transition-colors"
                                >
                                  {name}
                                </button>
                              ))
                            }
                            {options.filter(n => n.toLowerCase().includes(search.toLowerCase()) && !picks.includes(n)).length === 0 && (
                              <p className="px-3 py-2 text-slate-500 text-sm">No players found</p>
                            )}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Save button */}
                    {allFilled && hasChanged && !capData.is_locked && (
                      <button
                        onClick={() => submitCapPrediction(cat)}
                        disabled={capSubmitting === cat}
                        className={`w-full py-2.5 rounded-lg font-medium transition-all disabled:opacity-50 flex items-center justify-center gap-2 text-sm ${
                          cat === "top_scorer" ? "bg-orange-500 hover:bg-orange-600 text-white" :
                          cat === "top_wicket_taker" ? "bg-purple-500 hover:bg-purple-600 text-white" :
                          cat === "top_catches" ? "bg-cyan-500 hover:bg-cyan-600 text-white" :
                          "bg-emerald-500 hover:bg-emerald-600 text-white"
                        }`}
                        data-testid={`cap-save-${cat}`}
                      >
                        {capSubmitting === cat ? <Loader2 className="animate-spin" size={16} /> : <Save size={16} />}
                        Save {label}
                      </button>
                    )}

                    {/* Saved indicator */}
                    {allFilled && !hasChanged && (
                      <div className="flex items-center gap-2 text-emerald-400 text-xs mt-1">
                        <CheckCircle size={14} /> Saved
                      </div>
                    )}

                    {/* Poll - shows player popularity, NOT position */}
                    {poll && poll.total > 0 && (
                      <div className="mt-3 pt-3 border-t border-slate-700/50">
                        <p className="text-slate-500 text-xs mb-1.5 flex items-center gap-1">
                          <Users size={10} /> {poll.total} predictions — popularity (position hidden)
                        </p>
                        {Object.entries(poll.votes).slice(0, 5).map(([name, count]) => {
                          const pct = Math.round((count / (poll.total * 3)) * 100);
                          const isMyPick = picks.includes(name);
                          return (
                            <div key={name} className="relative rounded-lg overflow-hidden mb-1">
                              <div className={`absolute inset-0 ${isMyPick ? (
                                cat === "top_scorer" ? "bg-orange-500/20" :
                                cat === "top_wicket_taker" ? "bg-purple-500/20" :
                                cat === "top_catches" ? "bg-cyan-500/20" : "bg-emerald-500/20"
                              ) : "bg-slate-700/30"}`} style={{ width: `${Math.min(pct, 100)}%`, transition: "width 0.5s" }} />
                              <div className="relative px-3 py-1.5 flex items-center justify-between">
                                <span className={`text-xs ${isMyPick ? (
                                  cat === "top_scorer" ? "text-orange-400 font-medium" :
                                  cat === "top_wicket_taker" ? "text-purple-400 font-medium" :
                                  cat === "top_catches" ? "text-cyan-400 font-medium" : "text-emerald-400 font-medium"
                                ) : "text-slate-400"}`}>
                                  {isMyPick && <CheckCircle size={10} className="inline mr-1" />}{name}
                                </span>
                                <span className="text-xs font-mono text-slate-500">{pct}%</span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </>
          ) : (
            <div className="text-center py-8">
              <p className="text-slate-400">Failed to load cap predictions. Try refreshing.</p>
            </div>
          )}
        </div>
      )}

      {/* ==================== RANKINGS TAB ==================== */}
      {activeTab === "rankings" && (
        <div className="space-y-4" data-testid="rankings-tab">
          <div className="bg-gradient-to-r from-cyan-500/10 to-emerald-500/10 border border-cyan-500/30 rounded-xl p-4">
            <h3 className="text-white font-semibold flex items-center gap-2 mb-1">
              <Trophy size={18} className="text-cyan-400" /> Predictor Rankings
            </h3>
            <p className="text-slate-400 text-sm">Who predicted correctly the most across all match predictions</p>
          </div>

          {!predLeaderboard ? (
            <div className="flex justify-center py-8"><Loader2 className="animate-spin text-cyan-400" size={32} /></div>
          ) : predLeaderboard.length === 0 ? (
            <div className="text-center py-8 bg-slate-800/50 rounded-xl border border-slate-700/50">
              <Trophy className="mx-auto text-slate-600 mb-3" size={40} />
              <p className="text-slate-400">No predictions graded yet</p>
            </div>
          ) : (
            <div className="space-y-2">
              {predLeaderboard.map((entry, i) => (
                <div
                  key={entry.user_id}
                  className={`flex items-center justify-between p-3.5 rounded-xl ${
                    i === 0 ? "bg-yellow-500/10 border border-yellow-500/30" :
                    i === 1 ? "bg-slate-400/10 border border-slate-400/30" :
                    i === 2 ? "bg-orange-500/10 border border-orange-500/30" :
                    "bg-slate-800/50 border border-slate-700/30"
                  }`}
                  data-testid={`pred-rank-${i}`}
                >
                  <div className="flex items-center gap-3">
                    <span className={`w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold ${
                      i === 0 ? "bg-yellow-500 text-black" :
                      i === 1 ? "bg-slate-400 text-black" :
                      i === 2 ? "bg-orange-500 text-black" :
                      "bg-slate-700 text-slate-300"
                    }`}>{entry.rank}</span>
                    <div>
                      <span className="text-white font-medium">{entry.team_name}</span>
                      <p className="text-slate-500 text-xs">{entry.correct}/{entry.total} correct &bull; {entry.accuracy}% accuracy</p>
                    </div>
                  </div>
                  <span className="font-mono font-semibold text-cyan-400">{entry.prediction_points} pts</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ==================== ADMIN TAB ==================== */}
      {activeTab === "admin" && canManagePredictions && (
        <div className="space-y-6">
          {/* Create Questions */}
          <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4">
            <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
              <Plus size={18} className="text-emerald-400" />
              Create Prediction Questions
            </h3>

            <div className="mb-4">
              <label className="text-slate-400 text-sm mb-2 block">Select Match</label>
              
              {/* Bonus Mode Toggle */}
              <div className="flex items-center gap-3 mb-3 p-3 bg-slate-900/50 rounded-lg border border-slate-700">
                <input
                  type="checkbox"
                  id="bonus-mode"
                  checked={isBonusMode}
                  onChange={(e) => {
                    setIsBonusMode(e.target.checked);
                    if (e.target.checked) {
                      setAdminMatchId("");
                      setAdminMatchOptions(null);
                    }
                  }}
                  className="w-4 h-4 accent-amber-500"
                  data-testid="bonus-mode-checkbox"
                />
                <label htmlFor="bonus-mode" className="text-slate-300 text-sm">
                  <span className="font-medium text-amber-400">Bonus Question Mode</span>
                  <span className="text-slate-400 block text-xs">Create questions not tied to a specific match (e.g., "Who will top the table?")</span>
                </label>
              </div>
              
              {/* Match selector - hidden in bonus mode */}
              {!isBonusMode && (
                <select
                  value={adminMatchId}
                  onChange={(e) => handleAdminMatchSelect(e.target.value)}
                  className="w-full px-4 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white text-sm"
                  data-testid="admin-match-select"
                >
                  <option value="">Select a match...</option>
                  {allMatchesForAdmin.map(m => (
                    <option key={m.match_id} value={m.match_id}>
                      {m.team1} vs {m.team2} - {m.date}{m.status !== "upcoming" ? ` [${m.status.toUpperCase()}]` : ""}
                    </option>
                  ))}
                </select>
              )}
            </div>

            {loadingOptions && (
              <div className="flex items-center gap-2 text-slate-400 text-sm py-4">
                <Loader2 className="animate-spin" size={16} /> Loading players and teams...
              </div>
            )}

            {/* Bonus Mode - Show question types without match */}
            {isBonusMode && (
              <>
                <div className="mb-4 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg text-sm text-amber-300">
                  <strong>Bonus Mode:</strong> Creating questions not tied to any match. Perfect for weekly/tournament-wide predictions.
                </div>
                
                <div className="mb-4">
                  <p className="text-slate-400 text-xs font-medium uppercase tracking-wider mb-2">Add Bonus Question</p>
                  <div className="flex flex-wrap gap-2">
                    {/* Only show question types suitable for bonus mode */}
                    {["yes_no", "range", "over_under", "bonus", "custom"].map(key => (
                      <button
                        key={key}
                        onClick={() => addQuestion(key)}
                        className="px-3 py-2 bg-amber-600/20 hover:bg-amber-600/30 text-amber-300 border border-amber-500/30 rounded-lg text-xs font-medium transition-all flex items-center gap-1.5"
                        data-testid={`add-bonus-q-${key}`}
                      >
                        <Plus size={14} /> {QUESTION_TYPE_LABELS[key]}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Bonus Questions to Create */}
                {adminQuestions.length > 0 && (
                  <div className="space-y-3 mb-4">
                    <p className="text-slate-400 text-xs font-medium uppercase tracking-wider">Bonus Questions to Create</p>
                    {adminQuestions.map((q, idx) => (
                      <div key={idx} className="bg-slate-900/50 rounded-xl p-4 border border-amber-500/30">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-amber-400 text-sm font-medium">#{idx + 1} {QUESTION_TYPE_LABELS[q.question_type]}</span>
                          <div className="flex items-center gap-2">
                            <select
                              value={q.points}
                              onChange={(e) => updateQuestionPoints(idx, parseInt(e.target.value))}
                              className="px-2 py-1 bg-slate-800 border border-slate-600 rounded text-white text-xs"
                              data-testid={`bonus-q-points-${idx}`}
                            >
                              {VALID_POINTS.map(p => (<option key={p} value={p}>{p} pts</option>))}
                            </select>
                            <button onClick={() => removeQuestion(idx)} className="p-1 text-red-400 hover:bg-red-500/20 rounded">
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </div>

                        {/* Editable question text */}
                        <input
                          type="text"
                          value={q.question_text || ""}
                          onChange={(e) => {
                            setAdminQuestions(prev => {
                              const updated = [...prev];
                              updated[idx] = { ...updated[idx], question_text: e.target.value };
                              return updated;
                            });
                          }}
                          placeholder="Enter question text..."
                          className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white text-sm placeholder-slate-500 focus:border-amber-500 focus:outline-none mb-2"
                          data-testid={`bonus-q-text-${idx}`}
                        />

                        {/* Options input for bonus/range/custom */}
                        {(q.question_type === "range" || q.question_type === "custom" || q.question_type === "bonus") && (
                          <div className="mb-2">
                            <input
                              type="text"
                              placeholder="Options (comma-separated): e.g. 0-5, 6-10, 11-15, 16+"
                              value={q.optionsText !== undefined ? q.optionsText : q.options.join(", ")}
                              onChange={(e) => {
                                setAdminQuestions(prev => {
                                  const updated = [...prev];
                                  updated[idx] = { ...updated[idx], optionsText: e.target.value };
                                  return updated;
                                });
                              }}
                              onBlur={(e) => {
                                // Split options on blur
                                const opts = e.target.value.split(",").map(o => o.trim()).filter(Boolean);
                                setAdminQuestions(prev => {
                                  const updated = [...prev];
                                  updated[idx] = { ...updated[idx], options: opts };
                                  return updated;
                                });
                              }}
                              className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white text-sm placeholder-slate-500 focus:border-amber-500 focus:outline-none"
                              data-testid={`bonus-q-options-${idx}`}
                            />
                            <p className="text-slate-500 text-[10px] mt-1">Separate options with commas. Press Tab or click outside to confirm.</p>
                          </div>
                        )}

                        {/* Over/Under threshold */}
                        {q.question_type === "over_under" && (
                          <div className="mb-2">
                            <label className="text-slate-400 text-xs mb-1 block">Threshold Value</label>
                            <input
                              type="text"
                              placeholder="Enter threshold (e.g., 4, 150)"
                              value={q.threshold || ""}
                              onChange={(e) => {
                                setAdminQuestions(prev => {
                                  const updated = [...prev];
                                  updated[idx] = { ...updated[idx], threshold: e.target.value };
                                  return updated;
                                });
                              }}
                              className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white text-sm placeholder-slate-500 focus:border-amber-500 focus:outline-none"
                              data-testid={`bonus-q-threshold-${idx}`}
                            />
                            {q.threshold && (
                              <p className="text-slate-500 text-xs mt-1">Options: "Under {q.threshold}" and "Over {q.threshold}"</p>
                            )}
                          </div>
                        )}

                        {q.question_type === "yes_no" && (
                          <p className="text-slate-400 text-xs">Options: Yes, No (auto-generated)</p>
                        )}
                      </div>
                    ))}
                    <button
                      onClick={createQuestions}
                      disabled={creatingQuestions}
                      className="w-full py-3 bg-amber-500 text-black font-bold rounded-xl hover:bg-amber-400 disabled:opacity-50 flex items-center justify-center gap-2"
                      data-testid="create-bonus-questions-btn"
                    >
                      {creatingQuestions ? <Loader2 className="animate-spin" size={18} /> : <Save size={18} />}
                      Create {adminQuestions.length} Bonus Question(s)
                    </button>
                  </div>
                )}
              </>
            )}

            {adminMatchOptions && !isBonusMode && (
              <>
                <div className="mb-4 text-sm text-slate-400">
                  <span className="text-white font-medium">{adminMatchOptions.team1}</span> vs <span className="text-white font-medium">{adminMatchOptions.team2}</span>
                  {" "}&mdash; {adminMatchOptions.remaining_slots} question slot(s) remaining (max {adminMatchOptions.max_questions})
                </div>

                {existingQuestions.length > 0 && (
                  <div className="mb-4 space-y-2">
                    <p className="text-slate-400 text-xs font-medium uppercase tracking-wider">Existing Questions</p>
                    {existingQuestions.map(q => (
                      <div key={q.id} className="flex items-center justify-between bg-slate-900/50 rounded-lg p-3 border border-slate-700/50">
                        <div className="flex-1 min-w-0">
                          <span className="text-white text-sm">{q.question_text}</span>
                          <span className="text-slate-500 text-xs ml-2">({q.points} pts)</span>
                          <span className="text-slate-500 text-xs ml-2">{q.participant_count || 0} votes</span>
                          {q.is_graded && <span className="text-emerald-400 text-xs ml-2">Graded</span>}
                        </div>
                        {!q.is_graded && (
                          <button onClick={() => deleteQuestion(q.id)} className="p-1 text-red-400 hover:bg-red-500/20 rounded" data-testid={`delete-q-${q.id}`}>
                            <Trash2 size={14} />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {adminMatchOptions.remaining_slots > adminQuestions.length && (
                  <div className="mb-4">
                    <p className="text-slate-400 text-xs font-medium uppercase tracking-wider mb-2">Add Question</p>
                    <div className="flex flex-wrap gap-2">
                      {Object.entries(QUESTION_TYPE_LABELS).map(([key, label]) => (
                        <button
                          key={key}
                          onClick={() => addQuestion(key)}
                          className="px-3 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg text-xs font-medium transition-all flex items-center gap-1.5"
                          data-testid={`add-q-${key}`}
                        >
                          <Plus size={14} /> {label}
                          <span className="text-slate-400">({(adminMatchOptions.options_by_type[key] || []).length})</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {adminQuestions.length > 0 && (
                  <div className="space-y-3 mb-4">
                    <p className="text-slate-400 text-xs font-medium uppercase tracking-wider">New Questions to Create</p>
                    {adminQuestions.map((q, idx) => (
                      <div key={idx} className="bg-slate-900/50 rounded-xl p-4 border border-emerald-500/30">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-emerald-400 text-sm font-medium">#{q.order || idx + 1} {QUESTION_TYPE_LABELS[q.question_type]}</span>
                          <div className="flex items-center gap-2">
                            <select
                              value={q.points}
                              onChange={(e) => updateQuestionPoints(idx, parseInt(e.target.value))}
                              className="px-2 py-1 bg-slate-800 border border-slate-600 rounded text-white text-xs"
                              data-testid={`q-points-${idx}`}
                            >
                              {VALID_POINTS.map(p => (<option key={p} value={p}>{p} pts</option>))}
                            </select>
                            <button onClick={() => removeQuestion(idx)} className="p-1 text-red-400 hover:bg-red-500/20 rounded">
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </div>

                        {/* Editable question text */}
                        <input
                          type="text"
                          value={q.question_text || ""}
                          onChange={(e) => {
                            setAdminQuestions(prev => {
                              const updated = [...prev];
                              updated[idx] = { ...updated[idx], question_text: e.target.value };
                              return updated;
                            });
                          }}
                          placeholder={QUESTION_TYPE_LABELS[q.question_type] || "Type your question..."}
                          className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white text-sm placeholder-slate-500 focus:border-emerald-500 focus:outline-none mb-2"
                          data-testid={`q-text-${idx}`}
                        />

                        {/* Editable options for new types */}
                        {(q.question_type === "range" || q.question_type === "custom" || q.question_type === "bonus") && (
                          <div className="mb-2">
                            <input
                              type="text"
                              placeholder="Options (comma-separated): e.g. 0-5, 6-10, 11-15, 16+"
                              value={q.optionsText !== undefined ? q.optionsText : q.options.join(", ")}
                              onChange={(e) => {
                                setAdminQuestions(prev => {
                                  const updated = [...prev];
                                  updated[idx] = { ...updated[idx], optionsText: e.target.value };
                                  return updated;
                                });
                              }}
                              onBlur={(e) => {
                                // Split options on blur
                                const opts = e.target.value.split(",").map(o => o.trim()).filter(Boolean);
                                setAdminQuestions(prev => {
                                  const updated = [...prev];
                                  updated[idx] = { ...updated[idx], options: opts };
                                  return updated;
                                });
                              }}
                              className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white text-sm placeholder-slate-500 focus:border-emerald-500 focus:outline-none"
                              data-testid={`q-options-${idx}`}
                            />
                            <p className="text-slate-500 text-[10px] mt-1">Separate options with commas. Press Tab or click outside to confirm.</p>
                          </div>
                        )}

                        {/* Over/Under threshold input */}
                        {q.question_type === "over_under" && (
                          <div className="mb-2">
                            <label className="text-slate-400 text-xs mb-1 block">Threshold Value (e.g., "4" for "First wicket before over 4")</label>
                            <input
                              type="text"
                              placeholder="Enter threshold (e.g., 4, 150, 10.5)"
                              value={q.threshold || ""}
                              onChange={(e) => {
                                setAdminQuestions(prev => {
                                  const updated = [...prev];
                                  updated[idx] = { ...updated[idx], threshold: e.target.value };
                                  return updated;
                                });
                              }}
                              className="w-full px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white text-sm placeholder-slate-500 focus:border-emerald-500 focus:outline-none"
                              data-testid={`q-threshold-${idx}`}
                            />
                            {q.threshold && (
                              <p className="text-slate-500 text-xs mt-1">
                                Will generate options: "Under {q.threshold}" and "Over {q.threshold}"
                              </p>
                            )}
                          </div>
                        )}

                        {/* Yes/No auto-shows options */}
                        {q.question_type === "yes_no" && (
                          <p className="text-slate-400 text-xs mb-2">Options: Yes, No (auto-generated)</p>
                        )}

                        {q.options.length > 0 && (
                          <>
                            <p className="text-slate-400 text-xs mb-1">Options ({q.options.length}):</p>
                            <div className="flex flex-wrap gap-1.5">
                              {q.options.slice(0, 15).map((opt, oi) => (
                                <span key={oi} className="px-2 py-1 bg-slate-800 text-slate-300 rounded text-xs">{opt}</span>
                              ))}
                              {q.options.length > 15 && <span className="text-slate-500 text-xs">+{q.options.length - 15} more</span>}
                            </div>
                          </>
                        )}
                      </div>
                    ))}
                    <button
                      onClick={createQuestions}
                      disabled={creatingQuestions}
                      className="w-full py-3 bg-emerald-500 text-white rounded-xl font-semibold hover:bg-emerald-600 disabled:opacity-50 flex items-center justify-center gap-2"
                      data-testid="create-questions-btn"
                    >
                      {creatingQuestions ? <Loader2 className="animate-spin" size={18} /> : <Save size={18} />}
                      Create {adminQuestions.length} Question(s)
                    </button>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Grade Predictions */}
          <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4">
            <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
              <Unlock size={18} className="text-blue-400" />
              Unlock / Lock Predictions
            </h3>
            <p className="text-slate-400 text-sm mb-3">Override the automatic time-based lock for a match. Useful when you need to keep predictions open longer.</p>
            <div className="flex flex-wrap gap-2">
              {matchesWithQuestions.map(m => (
                <button
                  key={m.match_id}
                  onClick={() => toggleMatchLock(m.match_id)}
                  data-testid={`toggle-lock-${m.match_id}`}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-2 ${
                    m.is_locked
                      ? "bg-red-500/20 text-red-400 border border-red-500/30 hover:bg-red-500/30"
                      : "bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 hover:bg-emerald-500/30"
                  }`}
                >
                  {m.is_locked ? <Lock size={14} /> : <Unlock size={14} />}
                  {m.team1} vs {m.team2}
                  <span className="text-xs opacity-70">{m.is_locked ? "LOCKED" : "OPEN"}</span>
                </button>
              ))}
              {matchesWithQuestions.length === 0 && <p className="text-slate-500 text-sm">No matches with prediction questions.</p>}
            </div>
          </div>

          {/* Grade Predictions */}
          <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4">
            <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
              <CheckCircle size={18} className="text-amber-400" />
              Grade Predictions
            </h3>
            <div className="mb-4">
              <label className="text-slate-400 text-sm mb-2 block">Select Completed Match</label>
              <select
                value={gradingMatchId}
                onChange={(e) => handleGradingMatchSelect(e.target.value)}
                className="w-full px-4 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white text-sm"
                data-testid="grading-match-select"
              >
                <option value="">Select a match...</option>
                {completedMatchesForGrading.map(m => (
                  <option key={m.match_id} value={m.match_id}>
                    {m.team1} vs {m.team2} - {m.date}
                  </option>
                ))}
              </select>
            </div>

            {loadingGrading && (
              <div className="flex items-center gap-2 text-slate-400 text-sm py-4">
                <Loader2 className="animate-spin" size={16} /> Auto-detecting answers from scorecard...
              </div>
            )}

            {gradingData && gradingData.questions && gradingData.questions.length > 0 && (
              <div className="space-y-4">
                {gradingData.questions.map(q => {
                  const autoInfo = gradingData.auto_answers?.[q.id];
                  const currentAnswer = gradingAnswers[q.id] || {};
                  const hasTiedWinners = autoInfo?.auto && (autoInfo?.answers || []).length > 1;
                  const hasAutoAnswer = autoInfo?.auto && (autoInfo?.answers || []).length >= 1;
                  return (
                    <div key={q.id} className="bg-slate-900/50 rounded-xl p-4 border border-slate-700/50" data-testid={`grading-question-${q.id}`}>
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <p className="text-white text-sm font-medium">{q.question_text}</p>
                          <p className="text-slate-500 text-xs">{q.points} pts &bull; {q.participant_count || 0} participants</p>
                        </div>
                        {autoInfo?.auto && (
                          <span className="px-2 py-0.5 bg-emerald-500/20 text-emerald-400 text-xs rounded-full flex-shrink-0">Auto-detected</span>
                        )}
                      </div>
                      {autoInfo?.note && <p className="text-xs text-amber-400 mb-2">{autoInfo.note}</p>}
                      <label className="flex items-center gap-2 mb-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={currentAnswer.is_voided || false}
                          onChange={(e) => setGradingAnswers(prev => ({
                            ...prev, [q.id]: { ...prev[q.id], is_voided: e.target.checked }
                          }))}
                          className="rounded border-slate-600"
                        />
                        <span className="text-xs text-amber-400">Void question (all participants get points)</span>
                      </label>
                      {!currentAnswer.is_voided && (
                        <>
                          {/* Auto-detected answers: show as chips */}
                          {hasAutoAnswer && (
                            <div className="mb-2" data-testid={`auto-answers-${q.id}`}>
                              <p className="text-xs text-slate-400 mb-1.5">
                                {hasTiedWinners ? (
                                  <span className="text-amber-400 font-medium">Tied Winners ({autoInfo.answers.length}) — all will be awarded points:</span>
                                ) : (
                                  <span className="text-emerald-400 font-medium">Correct Answer:</span>
                                )}
                              </p>
                              <div className="flex flex-wrap gap-2">
                                {(autoInfo?.answers || []).map(ans => (
                                  <span key={ans} className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium ${
                                    hasTiedWinners
                                      ? "bg-amber-500/20 text-amber-300 border border-amber-500/40"
                                      : "bg-emerald-500/20 text-emerald-300 border border-emerald-500/40"
                                  }`}>
                                    <CheckCircle size={14} />
                                    {ans}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                          {/* Manual override dropdown — always shown for non-auto or as fallback */}
                          {!hasAutoAnswer && (
                            <select
                              value={currentAnswer.answer || ""}
                              onChange={(e) => setGradingAnswers(prev => ({
                                ...prev, [q.id]: { ...prev[q.id], answer: e.target.value, answers: e.target.value ? [e.target.value] : [] }
                              }))}
                              className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white text-sm"
                              data-testid={`grade-answer-${q.id}`}
                            >
                              <option value="">Select correct answer...</option>
                              {q.options.map(opt => (<option key={opt} value={opt}>{opt}</option>))}
                            </select>
                          )}
                        </>
                      )}
                    </div>
                  );
                })}
                <button
                  onClick={gradeMatch}
                  disabled={grading}
                  className="w-full py-3 bg-amber-500 text-white rounded-xl font-semibold hover:bg-amber-600 disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="grade-btn"
                >
                  {grading ? <Loader2 className="animate-spin" size={18} /> : <CheckCircle size={18} />}
                  Grade Predictions
                </button>
              </div>
            )}

            {gradingData && (!gradingData.questions || gradingData.questions.length === 0) && (
              <p className="text-slate-400 text-sm text-center py-4">No ungraded questions for this match.</p>
            )}
          </div>

          {/* Bonus Questions Grading */}
          <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4">
            <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
              <Star size={18} className="text-amber-400" />
              Grade Bonus Questions
            </h3>
            <p className="text-slate-400 text-sm mb-3">Grade tournament-wide questions not tied to specific matches.</p>
            <button
              onClick={async () => {
                try {
                  setBonusLoading(true);
                  const token = await user.getIdToken(true);
                  const res = await axios.get(`${API}/admin/predictions/bonus-questions`, { headers: { Authorization: `Bearer ${token}` } });
                  setBonusQuestions(res.data.questions || []);
                  // Pre-populate bonus grading answers
                  const preAnswers = {};
                  (res.data.questions || []).forEach(q => {
                    if (!q.is_graded) {
                      preAnswers[q.id] = { answer: "", answers: [], is_voided: false };
                    }
                  });
                  setBonusGradingAnswers(preAnswers);
                } catch (e) {
                  setError(e.response?.data?.detail || "Failed to load bonus questions");
                } finally {
                  setBonusLoading(false);
                }
              }}
              className="px-4 py-2 bg-amber-500/20 text-amber-400 rounded-lg text-sm font-medium hover:bg-amber-500/30 transition-all flex items-center gap-2 mb-4"
              data-testid="load-bonus-questions"
            >
              {bonusLoading ? <Loader2 className="animate-spin" size={16} /> : <RefreshCw size={16} />}
              Load Bonus Questions
            </button>

            {/* Lock All Bonus Questions Button */}
            <div className="flex gap-2 mb-4">
              <button
                onClick={async () => {
                  try {
                    const token = await user.getIdToken(true);
                    const res = await axios.post(`${API}/admin/predictions/lock-all-bonus`, {}, { headers: { Authorization: `Bearer ${token}` } });
                    setSuccess(res.data.message);
                    setTimeout(() => setSuccess(""), 3000);
                  } catch (e) {
                    setError(e.response?.data?.detail || "Failed to lock bonus questions");
                  }
                }}
                className="px-4 py-2 bg-red-500/20 text-red-400 rounded-lg text-sm font-medium hover:bg-red-500/30 transition-all flex items-center gap-2"
                data-testid="lock-all-bonus"
              >
                <Lock size={16} />
                Lock All
              </button>
              <button
                onClick={async () => {
                  try {
                    const token = await user.getIdToken(true);
                    const res = await axios.delete(`${API}/admin/predictions/bonus-questions`, { headers: { Authorization: `Bearer ${token}` } });
                    setSuccess(res.data.message);
                    setBonusQuestions([]);
                    setTimeout(() => setSuccess(""), 3000);
                  } catch (e) {
                    setError(e.response?.data?.detail || "Failed to delete bonus questions");
                  }
                }}
                className="px-4 py-2 bg-red-500/20 text-red-400 rounded-lg text-sm font-medium hover:bg-red-500/30 transition-all flex items-center gap-2"
                data-testid="delete-all-bonus"
              >
                <Trash2 size={16} />
                Delete All Bonus
              </button>
            </div>

            {bonusQuestions.length > 0 && (
              <div className="space-y-4">
                <div className="text-xs text-slate-400 mb-2">
                  Found {bonusQuestions.filter(q => !q.is_graded).length} ungraded bonus question(s)
                </div>
                {bonusQuestions.filter(q => !q.is_graded).map(q => (
                  <div key={q.id} className="bg-slate-900/50 rounded-xl p-4 border border-amber-500/30" data-testid={`bonus-grading-${q.id}`}>
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1">
                        <p className="text-white text-sm font-medium">{q.question_text}</p>
                        <p className="text-slate-500 text-xs">{q.points} pts &bull; {q.participant_count || 0} participants</p>
                      </div>
                      <div className="flex items-center gap-2">
                        {/* Individual Lock Toggle */}
                        <button
                          onClick={async () => {
                            try {
                              const token = await user.getIdToken(true);
                              const res = await axios.post(`${API}/admin/predictions/toggle-bonus-lock/${q.id}`, {}, { headers: { Authorization: `Bearer ${token}` } });
                              setSuccess(res.data.message);
                              // Refresh bonus questions
                              const refreshRes = await axios.get(`${API}/admin/predictions/bonus-questions`, { headers: { Authorization: `Bearer ${token}` } });
                              setBonusQuestions(refreshRes.data.questions || []);
                              setTimeout(() => setSuccess(""), 3000);
                            } catch (e) {
                              setError(e.response?.data?.detail || "Failed to toggle lock");
                            }
                          }}
                          className={`px-2 py-1 rounded text-xs font-medium flex items-center gap-1 ${
                            q.is_locked 
                              ? "bg-red-500/20 text-red-400 border border-red-500/30" 
                              : "bg-emerald-500/20 text-emerald-400 border border-emerald-500/30"
                          }`}
                          title={q.is_locked ? "Click to unlock" : "Click to lock"}
                        >
                          {q.is_locked ? <Lock size={12} /> : <Unlock size={12} />}
                          {q.is_locked ? "Locked" : "Open"}
                        </button>
                        <span className="px-2 py-0.5 bg-amber-500/20 text-amber-400 text-xs rounded-full">Bonus</span>
                      </div>
                    </div>
                    <label className="flex items-center gap-2 mb-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={bonusGradingAnswers[q.id]?.is_voided || false}
                        onChange={(e) => setBonusGradingAnswers(prev => ({
                          ...prev, [q.id]: { ...prev[q.id], is_voided: e.target.checked }
                        }))}
                        className="rounded border-slate-600"
                      />
                      <span className="text-xs text-amber-400">Void question (all participants get points)</span>
                    </label>
                    {!bonusGradingAnswers[q.id]?.is_voided && (
                      <select
                        value={bonusGradingAnswers[q.id]?.answer || ""}
                        onChange={(e) => setBonusGradingAnswers(prev => ({
                          ...prev, [q.id]: { ...prev[q.id], answer: e.target.value, answers: e.target.value ? [e.target.value] : [] }
                        }))}
                        className="w-full px-3 py-2 bg-slate-800 border border-slate-600 rounded-lg text-white text-sm"
                        data-testid={`bonus-grade-answer-${q.id}`}
                      >
                        <option value="">Select correct answer...</option>
                        {(q.options || []).map(opt => (<option key={opt} value={opt}>{opt}</option>))}
                      </select>
                    )}
                  </div>
                ))}
                {bonusQuestions.filter(q => !q.is_graded).length > 0 && (
                  <button
                    onClick={async () => {
                      if (Object.keys(bonusGradingAnswers).length === 0) {
                        setError("No answers to grade"); return;
                      }
                      try {
                        setBonusGrading(true);
                        const token = await user.getIdToken(true);
                        const result = await axios.post(`${API}/admin/predictions/grade-bonus`, { answers: bonusGradingAnswers }, { headers: { Authorization: `Bearer ${token}` } });
                        const d = result.data;
                        setSuccess(`Graded ${d.total_graded} bonus predictions! ${d.correct} correct, ${d.total_points_awarded} pts awarded.`);
                        setBonusQuestions([]);
                        setBonusGradingAnswers({});
                        setTimeout(() => setSuccess(""), 5000);
                      } catch (e) {
                        setError(e.response?.data?.detail || "Failed to grade bonus questions");
                      } finally {
                        setBonusGrading(false);
                      }
                    }}
                    disabled={bonusGrading}
                    className="w-full py-3 bg-amber-500 text-white rounded-xl font-semibold hover:bg-amber-600 disabled:opacity-50 flex items-center justify-center gap-2"
                    data-testid="grade-bonus-btn"
                  >
                    {bonusGrading ? <Loader2 className="animate-spin" size={18} /> : <CheckCircle size={18} />}
                    Grade Bonus Questions
                  </button>
                )}
                {bonusQuestions.filter(q => q.is_graded).length > 0 && (
                  <div className="mt-4">
                    <p className="text-xs text-slate-500 mb-2">Already graded ({bonusQuestions.filter(q => q.is_graded).length}):</p>
                    {bonusQuestions.filter(q => q.is_graded).map(q => (
                      <div key={q.id} className="text-xs text-slate-500 bg-slate-900/30 rounded p-2 mb-1">
                        {q.question_text} → <span className="text-emerald-400">{q.correct_answer || "VOIDED"}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Cap Predictions Admin */}
          <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-white font-semibold flex items-center gap-2">
                <Award size={18} className="text-orange-400" />
                Tournament Stats (Live)
              </h3>
              <button
                onClick={fetchCapStats}
                disabled={capStatsLoading}
                className="text-xs text-cyan-400 hover:text-cyan-300 flex items-center gap-1"
                data-testid="refresh-cap-stats"
              >
                {capStatsLoading ? <Loader2 className="animate-spin" size={14} /> : <TrendingUp size={14} />}
                {capStats ? "Refresh" : "Load Stats"}
              </button>
            </div>

            {!capStats && !capStatsLoading && (
              <p className="text-slate-500 text-sm text-center py-3">Click "Load Stats" to view current tournament leaders</p>
            )}

            {capStats && (
              <div className="space-y-3">
                <div className="flex gap-3 text-xs text-slate-400 mb-2">
                  <span className="bg-slate-900/50 px-2 py-1 rounded">{capStats.matches_synced} matches synced</span>
                  <span className="bg-slate-900/50 px-2 py-1 rounded">{capStats.total_performances} performances</span>
                </div>
                {[
                  { key: "top_scorer", label: "Top Run Scorers", color: "text-orange-400", unit: "runs" },
                  { key: "top_wicket_taker", label: "Top Wicket Takers", color: "text-purple-400", unit: "wkts" },
                  { key: "top_catches", label: "Top Catches", color: "text-cyan-400", unit: "catches" },
                  { key: "top_dismissals", label: "Top Dismissals", color: "text-amber-400", unit: "dismissals" },
                ].map(({ key, label, color, unit }) => (
                  <div key={key} className="bg-slate-900/50 border border-slate-700 rounded-lg p-3">
                    <h4 className={`text-xs font-semibold ${color} mb-2`}>{label}</h4>
                    {(capStats[key] || []).length === 0 ? (
                      <p className="text-slate-600 text-xs">No data yet</p>
                    ) : (
                      <div className="space-y-1">
                        {(capStats[key] || []).map((p, i) => (
                          <div key={p.player_id} className={`flex items-center gap-2 text-sm ${i < 3 ? "font-medium" : "text-slate-500"}`}>
                            <span className={`w-5 h-5 rounded-full text-xs flex items-center justify-center font-bold ${
                              i === 0 ? "bg-amber-500/20 text-amber-400" : i === 1 ? "bg-slate-400/20 text-slate-300" : i === 2 ? "bg-orange-500/20 text-orange-400" : "bg-slate-800 text-slate-500"
                            }`}>{i + 1}</span>
                            <span className={`flex-1 truncate ${i < 3 ? "text-white" : "text-slate-400"}`}>{p.name}</span>
                            <span className="text-xs text-slate-500">{p.team}</span>
                            <span className={`font-mono text-sm ${i < 3 ? color : "text-slate-500"}`}>{p.value} {unit}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Cap Predictions Grade */}
          <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4">
            <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
              <Award size={18} className="text-orange-400" />
              Cap Predictions (Tournament)
            </h3>

            <div className="flex gap-3 mb-4">
              <button
                onClick={async () => {
                  try {
                    const token = await user.getIdToken(true);
                    const res = await axios.post(`${API}/admin/cap-predictions/toggle-lock`, {}, { headers: { Authorization: `Bearer ${token}` } });
                    setSuccess(res.data.message);
                    if (capData) fetchCapData();
                    setTimeout(() => setSuccess(""), 3000);
                  } catch (e) { setError(e.response?.data?.detail || "Failed to toggle lock"); }
                }}
                className="flex-1 py-2.5 bg-slate-700 text-white rounded-lg font-medium hover:bg-slate-600 transition-all flex items-center justify-center gap-2 text-sm"
                data-testid="toggle-cap-lock"
              >
                <Lock size={16} /> Toggle Lock / Unlock
              </button>
              <button
                onClick={async () => {
                  if (!window.confirm("Grade all cap predictions? This awards points and cannot be undone.")) return;
                  setGrading(true);
                  try {
                    const token = await user.getIdToken(true);
                    const res = await axios.post(`${API}/admin/cap-predictions/grade`, {}, { headers: { Authorization: `Bearer ${token}` } });
                    const r = res.data;
                    let msg = `Graded ${r.total_graded} predictions, ${r.total_points_awarded} pts awarded.\n`;
                    for (const [cat, info] of Object.entries(r.categories || {})) {
                      const names = (info.actual || []).map(a => typeof a === "string" ? a : a.name).join(", ");
                      msg += `${cat}: ${names} (${info.points} pts)\n`;
                    }
                    setSuccess(msg);
                  } catch (e) { setError(e.response?.data?.detail || "Failed to grade"); }
                  finally { setGrading(false); }
                }}
                disabled={grading}
                className="flex-1 py-2.5 bg-gradient-to-r from-orange-500 to-purple-500 text-white rounded-lg font-semibold hover:opacity-90 disabled:opacity-50 transition-all flex items-center justify-center gap-2 text-sm"
                data-testid="grade-caps-btn"
              >
                {grading ? <Loader2 className="animate-spin" size={16} /> : <CheckCircle size={16} />}
                Grade Cap Predictions
              </button>
            </div>
            <p className="text-slate-500 text-xs">Lock predictions before the final match, then grade after the last scorecard is synced.</p>
          </div>
        </div>
      )}

      {/* ==================== HISTORY TAB ==================== */}
      {activeTab === "history" && (
        <div className="space-y-4">
          {historyData.predictions.length === 0 ? (
            <div className="text-center py-12 bg-slate-800/50 rounded-xl border border-slate-700">
              <Award className="mx-auto text-slate-600 mb-4" size={48} />
              <p className="text-slate-400">No prediction history yet.</p>
              <p className="text-slate-500 text-sm mt-2">Make your first prediction to see it here!</p>
            </div>
          ) : (
            <div className="bg-slate-800/50 border border-slate-700 rounded-xl overflow-hidden">
              {historyData.predictions.map((pred, idx) => {
                const question = historyData.questions?.[pred.question_id];
                const match = historyData.matches?.[pred.match_id];
                return (
                  <div
                    key={`${pred.question_id}-${pred.user_id}`}
                    className={`px-4 py-3 flex items-center gap-3 ${idx > 0 ? "border-t border-slate-700/50" : ""}`}
                    data-testid={`history-row-${idx}`}
                  >
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                      pred.is_correct === true ? "bg-emerald-500/20"
                        : pred.is_correct === false ? "bg-red-500/20" : "bg-slate-600/50"
                    }`}>
                      {pred.is_correct === true ? <CheckCircle className="text-emerald-400" size={20} />
                        : pred.is_correct === false ? <XCircle className="text-red-400" size={20} />
                        : <Clock className="text-slate-400" size={20} />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-white text-sm font-medium truncate">{question?.question_text || "Question"}</p>
                      <p className="text-slate-400 text-xs">
                        {match?.team1 || "?"} vs {match?.team2 || "?"} &bull; <span className="text-white">{pred.answer}</span>
                      </p>
                    </div>
                    <div className={`text-right ${pred.points_earned > 0 ? "text-red-400" : "text-slate-500"}`}>
                      <span className="font-bold text-sm">
                        {pred.is_correct === null ? "Pending" : pred.points_earned > 0 ? `+${pred.points_earned}` : "0"}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* How It Works */}
      <div className="bg-slate-800/30 border border-slate-700/50 rounded-xl p-4">
        <h3 className="text-red-400 font-semibold flex items-center gap-2 mb-2">
          <Star size={16} /> How It Works
        </h3>
        <ul className="text-slate-400 text-sm space-y-1">
          <li>&bull; Answer prediction questions before the match starts</li>
          <li>&bull; See poll results after you vote</li>
          <li>&bull; Earn bonus points for each correct prediction (5-25 pts)</li>
          <li>&bull; Predictions lock automatically at match start time</li>
          <li>&bull; Points appear in <span className="text-red-400 font-medium">red</span> on the leaderboard</li>
        </ul>
      </div>
    </div>
    </div>
  );
}
